import { useState } from 'react';
import styles from './RemovalForm.module.css';

const REMOVAL_EMAIL = 'support@repbuddy.io';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export default function RemovalForm() {
  const [form, setForm] = useState({ name: '', email: '', company: '', profile: '', details: '' });
  const [confirm, setConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState('');

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  }

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = 'We need your name to locate your profile.';
    if (!form.email.trim()) e.email = 'An email is required.';
    else if (!EMAIL_RE.test(form.email.trim())) e.email = 'That email doesn’t look right.';
    if (!confirm) e.confirm = 'Please confirm this request is for your own information.';
    return e;
  }

  async function handleSubmit(evt) {
    evt.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;

    setSending(true);
    setSubmitError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/removal-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          profile: form.profile.trim(),
          details: form.details.trim(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong.');
      }
      setSent(true);
    } catch (err) {
      setSubmitError(
        err.message === 'Failed to fetch'
          ? `We couldn’t reach the server. Please try again, or email ${REMOVAL_EMAIL} directly.`
          : err.message
      );
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <section id="removal-form" className={styles.section}>
        <div className={styles.inner}>
          <div className={styles.success}>
            <span className={styles.successMark} aria-hidden="true">✓</span>
            <h2 className={styles.successHeading}>Removal request received.</h2>
            <p className={styles.successText}>
              Thanks! We've got your request and will process it. Once done, your profile
              won't be available in our products, and we'll confirm at the email you gave. If
              you have questions, reach us at{' '}
              <a className={styles.inlineLink} href={`mailto:${REMOVAL_EMAIL}`}>{REMOVAL_EMAIL}</a>.
            </p>
            <button className={styles.resetBtn} onClick={() => setSent(false)}>Submit another</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="removal-form" className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.card}>
          <h2 className={styles.heading}>Submit a removal request</h2>
          <p className={styles.note}>
            Give us enough to find you. The more detail, the faster we can locate and remove
            your profile.
          </p>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <div className={styles.grid2}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Full name</span>
                <input
                  className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                  type="text"
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  placeholder="Jordan Rivera"
                />
                {errors.name && <span className={styles.error}>{errors.name}</span>}
              </label>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>Email on the profile</span>
                <input
                  className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="you@company.com"
                />
                {errors.email && <span className={styles.error}>{errors.email}</span>}
              </label>
            </div>

            <div className={styles.grid2}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Company <span className={styles.optional}>optional</span></span>
                <input
                  className={styles.input}
                  type="text"
                  value={form.company}
                  onChange={(e) => update('company', e.target.value)}
                  placeholder="Where you work / worked"
                />
              </label>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>Profile URL <span className={styles.optional}>optional</span></span>
                <input
                  className={styles.input}
                  type="url"
                  value={form.profile}
                  onChange={(e) => update('profile', e.target.value)}
                  placeholder="LinkedIn or other profile link"
                />
              </label>
            </div>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>Anything else that helps locate you <span className={styles.optional}>optional</span></span>
              <textarea
                className={styles.textarea}
                rows={4}
                value={form.details}
                onChange={(e) => update('details', e.target.value)}
                placeholder="Past employers, job titles, other emails…"
              />
            </label>

            <label className={styles.confirmRow}>
              <input
                type="checkbox"
                checked={confirm}
                onChange={(e) => { setConfirm(e.target.checked); if (errors.confirm) setErrors((x) => ({ ...x, confirm: undefined })); }}
              />
              <span>I confirm this request is for my own information, or I'm authorized to make it.</span>
            </label>
            {errors.confirm && <span className={styles.error}>{errors.confirm}</span>}

            {submitError && <p className={styles.error} role="alert">{submitError}</p>}
            <button className={styles.submit} type="submit" disabled={sending}>
              {sending ? 'Submitting…' : 'Submit removal request'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
